import { z } from "zod";
import type { TenantCaps } from "../schemas/tenant.js";

export const PLAN_IDS = ["free", "starter", "pro", "scale"] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const planIdSchema = z.enum(PLAN_IDS);

export const PAID_PLAN_IDS = ["starter", "pro", "scale"] as const satisfies readonly PlanId[];
export type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

/** The period counters that a plan includes and bills. They are also the attribute names on the `PERIOD#` item. */
export const METERED_COUNTERS = ["messages", "writerCalls"] as const;
export type MeteredCounter = (typeof METERED_COUNTERS)[number];
export type MeteredCounts = Record<MeteredCounter, number>;

/** One overage unit is a tenth of a cent. Stripe's one overage price charges this per unit. */
export const OVERAGE_UNIT_MICROS = 1_000;
export const overageUnitsSchema = z.int().min(0).brand<"OverageUnits">();
export type OverageUnits = z.infer<typeof overageUnitsSchema>;

export function overageUnits(value: number): OverageUnits {
  return overageUnitsSchema.parse(value);
}

interface PlanFields {
  name: string;
  /** The price of one period, in whole dollars. */
  priceUsd: number;
  /** The allowance: what one period includes. */
  included: MeteredCounts;
  caps: TenantCaps;
}

export interface FreePlan extends PlanFields {
  id: "free";
  /** Free has no overage: it holds at the allowance. */
  overageUnits: null;
}

export interface PaidPlan extends PlanFields {
  id: PaidPlanId;
  /** The price of one message or written reply above the allowance, in overage units. */
  overageUnits: Record<MeteredCounter, OverageUnits>;
}

export type Plan = FreePlan | PaidPlan;

/** The one source of truth for plan numbers: limits, Stripe prices, the console, and the site read it. */
export const PLANS: { free: FreePlan } & Record<PaidPlanId, PaidPlan> = {
  free: {
    id: "free",
    name: "Free",
    priceUsd: 0,
    // A message has at most one written reply, so the written replies can never pass the messages.
    included: { messages: 100, writerCalls: 100 },
    overageUnits: null,
    caps: { messagesPerDay: 100, runsPerDay: 100, outboundPerDay: 100, writerCallsPerDay: 100 },
  },
  starter: {
    id: "starter",
    name: "Starter",
    priceUsd: 29,
    included: { messages: 2_000, writerCalls: 300 },
    overageUnits: { messages: overageUnits(15), writerCalls: overageUnits(60) },
    caps: { messagesPerDay: 1_000, runsPerDay: 1_000, outboundPerDay: 1_000, writerCallsPerDay: 500 },
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceUsd: 99,
    included: { messages: 7_000, writerCalls: 1_000 },
    overageUnits: { messages: overageUnits(13), writerCalls: overageUnits(50) },
    caps: { messagesPerDay: 3_000, runsPerDay: 3_000, outboundPerDay: 3_000, writerCallsPerDay: 1_500 },
  },
  scale: {
    id: "scale",
    name: "Scale",
    priceUsd: 399,
    included: { messages: 25_000, writerCalls: 4_000 },
    overageUnits: { messages: overageUnits(11), writerCalls: overageUnits(45) },
    caps: { messagesPerDay: 10_000, runsPerDay: 10_000, outboundPerDay: 10_000, writerCallsPerDay: 5_000 },
  },
};

export const MAX_BILL_LIMIT_USD = 100_000;
const DEFAULT_BILL_LIMIT_MULTIPLE = 3;

export function isPaidPlanId(plan: PlanId): plan is PaidPlanId {
  return plan !== "free";
}

export function defaultBillLimitUsd(plan: PaidPlan): number {
  return plan.priceUsd * DEFAULT_BILL_LIMIT_MULTIPLE;
}

/** The overage price of one message or written reply, in millionths of a dollar. */
export function overageMicros(plan: PaidPlan, counter: MeteredCounter): number {
  return plan.overageUnits[counter] * OVERAGE_UNIT_MICROS;
}
